import { FC, useMemo } from 'react';
import { useSource } from '../hooks/useSource';
import { shouldFetchSourceIndividually, useSourceIndex } from '../hooks/useSourceIndex';
import { useSourceTypes } from '../hooks/useSourceTypes';
import flatFileIcon from '../assets/images/connectors/flatfile.png';
import snowflakeIcon from '../assets/images/snowflake_logo.png';

interface SourceIconProps {
  sourceId: string;
  className?: string;
  size?: number;
}

export const SourceIcon: FC<SourceIconProps> = ({ sourceId, className = '', size = 20 }) => {
  // Some special sources (e.g. flat_files, snowflake) are virtual and not stored in the database
  const isVirtualSource = sourceId === 'flat_files' || sourceId === 'snowflake';

  // The shared list first: one request answers for every icon on the page. A list
  // of twenty sources rendered twenty of these, and each one used to fetch its own
  // source — the same page now makes a single call.
  const index = useSourceIndex({ enabled: !isVirtualSource });
  const indexedSource = isVirtualSource ? undefined : index.byId.get(sourceId);

  // Only ask for one source when the list genuinely can't answer: it hasn't arrived,
  // or it was truncated and this id fell outside it. If the list is complete and the
  // id isn't in it, the source doesn't exist and the request would only 404.
  const missingFromIndex = index.isComplete && !indexedSource;
  const needsIndividualFetch = shouldFetchSourceIndividually({
    isVirtual: isVirtualSource,
    foundInIndex: !!indexedSource,
    index,
  });
  const {
    data: fetchedSource,
    isLoading: isLoadingSource,
    error: sourceError,
  } = useSource(needsIndividualFetch ? sourceId : null);

  const { data: sourceTypes, isLoading: isLoadingSourceTypes } = useSourceTypes();

  const source = indexedSource ?? fetchedSource;
  // "We looked and it isn't there", however we found that out — the fallback that
  // reads the id as a source *type* keys off this.
  const notFound = !!sourceError || missingFromIndex;

  // Log errors for debugging (can be removed in production)
  if (sourceError && !isVirtualSource) {
    console.warn(`Failed to load source ${sourceId}:`, sourceError);
    // Check if it's a 404 - the source might not exist in the database
    if (sourceError instanceof Error && 'code' in sourceError && (sourceError as any).code === 404) {
      console.warn(`Source ${sourceId} not found - it may have been deleted or the ID is invalid`);
    }
  }

  const sourceName = useMemo(() => {
    if (sourceId === 'flat_files') {
      return 'Flat Files';
    }
    if (sourceId === 'snowflake') {
      return 'Snowflake';
    }
    return source?.name || sourceId;
  }, [sourceId, source?.name]);

  const iconPath = useMemo(() => {
    // For virtual sources, use hardcoded icons (matching frontend app behavior)
    if (sourceId === 'flat_files') {
      return flatFileIcon;
    }
    if (sourceId === 'snowflake') {
      return snowflakeIcon;
    }
    
    // If the source has a custom icon override, use it
    if (source?.icon_override) {
      return source.icon_override;
    }

    // For real sources, find the source type by the source's type field
    if (source && sourceTypes) {
      const sourceType = sourceTypes.find(st => st.id === source.type);
      return sourceType?.icon_path;
    }
    
    // Fallback: if the source could not be found but we have source types, try to find by
    // sourceId as source type ID
    // (This handles cases where the source_id might actually be a source type ID, or the source was deleted)
    if (notFound && sourceTypes) {
      const sourceType = sourceTypes.find(st => st.id === sourceId);
      if (sourceType) {
        return sourceType.icon_path;
      }
    }
    
    return null;
  }, [sourceId, source, sourceTypes, notFound]);

  // Show loading state (optional - you might want to show a placeholder)
  if ((!isVirtualSource && (index.isLoading || isLoadingSource)) || isLoadingSourceTypes) {
    return (
      <div 
        className={`inline-flex items-center justify-center rounded-full bg-gray-100 border border-gray-300 ${className}`}
        style={{ width: size, height: size }}
        title={sourceName}
      >
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" />
      </div>
    );
  }

  // If we have an icon path, display it
  if (iconPath) {
    return (
      <div 
        className={`inline-flex items-center justify-center rounded-full overflow-hidden ${className}`}
        style={{ width: size, height: size }}
        title={sourceName}
      >
        <img 
          src={iconPath} 
          alt={sourceName}
          style={{ width: size, height: size, objectFit: 'cover' }}
          className="rounded-full"
        />
      </div>
    );
  }

  // Fallback for sources without icons
  return (
    <div 
      className={`inline-flex items-center justify-center rounded-full bg-gray-100 border border-gray-300 ${className}`}
      style={{ width: size, height: size }}
      title={sourceName}
    >
      <span className="text-xs font-semibold text-gray-600" style={{ fontSize: size * 0.5 }}>
        {sourceName.charAt(0).toUpperCase()}
      </span>
    </div>
  );
};

